import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CameraIcon, CheckIcon, IdCardIcon, MapPinIcon, PlusIcon, SendIcon, ShieldCheckIcon, UploadIcon, UserRoundIcon, UsersRoundIcon, XIcon, BoxIcon } from "lucide-react";
import { editableFields, familyMembers, memberValues, verifiedAddresses, FamilyMember } from "../../data/carte";
const ease = [0.23, 1, 0.32, 1] as const;
const fieldIcons: Record<string, BoxIcon> = {
  prenom: UserRoundIcon,
  nom: IdCardIcon,
  genre: UsersRoundIcon,
  photo: CameraIcon,
  adresse: MapPinIcon
};
interface EditCardDialogProps {
  member: FamilyMember | null;
  onClose: () => void;
}
type Step = 'champs' | 'formulaire' | 'preuves' | 'confirmation';
export function EditCardDialog({
  member,
  onClose
}: EditCardDialogProps) {
  const [step, setStep] = useState<Step>('champs');
  const [selected, setSelected] = useState<string[]>([]);
  const [alsoFor, setAlsoFor] = useState<string[]>([]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [addressChoice, setAddressChoice] = useState<string>(verifiedAddresses[0].id);
  const [newAddress, setNewAddress] = useState('');
  const [proofs, setProofs] = useState<string[]>([]);
  const current = member ? memberValues[member.id] ?? {} : {};
  useEffect(() => {
    if (member) {
      setStep('champs');
      setSelected([]);
      setAlsoFor([]);
      setValues(memberValues[member.id] ?? {});
      setAddressChoice(verifiedAddresses[0].id);
      setNewAddress('');
      setProofs([]);
    }
  }, [member]);
  if (!member) return null;
  const chosen = editableFields.filter(field => selected.includes(field.id));
  const addressSelected = selected.includes('adresse');
  const newAddressChosen = addressSelected && addressChoice === 'nouvelle';
  const needProof = [...chosen.filter(field => field.requiresProof), ...(newAddressChosen ? [editableFields.find(field => field.id === 'adresse')!] : [])];
  const others = familyMembers.filter(item => item.id !== member.id);
  const toggle = (list: string[], id: string) => list.includes(id) ? list.filter(item => item !== id) : [...list, id];
  const next = () => {
    if (step === 'champs') setStep('formulaire');else if (step === 'formulaire') setStep('preuves');else setStep('confirmation');
  };
  const back = () => {
    if (step === 'formulaire') setStep('champs');else if (step === 'preuves') setStep('formulaire');else onClose();
  };
  const stepIndex = ['champs', 'formulaire', 'preuves'].indexOf(step);
  return <Shell title={`Modifier la carte${member.id === 'charlotte' ? '' : ` de ${member.firstName}`}`} onClose={onClose} data-id="element-522" data-mp="ez1rkei1s9ho">
      {step !== 'confirmation' && <ol className="mb-6 flex items-center gap-2" aria-label="Étapes" data-id="element-523" data-mp="50chh7we7tbz">
          {['Champs', 'Informations', 'Validation'].map((label, index) => <li key={label} className="flex flex-1 items-center gap-2" data-id="element-524" data-mp="2uqppod8ss822" data-mpi={index}>
              <span className={`grid h-7 w-7 shrink-0 place-items-center rounded-full text-xs font-bold ${index <= stepIndex ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-slate-400'}`} data-id="element-525" data-mp="2pq1z1sohdt8d">
                {index + 1}
              </span>
              <span className={`text-xs font-semibold ${index <= stepIndex ? 'text-ville-navy' : 'text-slate-400'}`} data-id="element-526" data-mp="2km96b9w408ti">
                {label}
              </span>
            </li>)}
        </ol>}

      <AnimatePresence mode="wait" data-id="element-527" data-mp="2uccam68ol975">
        <motion.div key={step} initial={{
        opacity: 0,
        x: 16
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: -16
      }} transition={{
        duration: 0.22,
        ease
      }} data-id="element-528" data-mp="2uddosjkzubt3">
          {step === 'champs' && <div data-id="element-529" data-mp="38c3jxto961et">
              <p className="text-sm text-slate-600" data-id="element-530" data-mp="389hd5w96rpxn">Quels renseignements souhaitez-vous modifier ?</p>

              <ul className="mt-4 grid grid-cols-2 gap-3" data-id="element-531" data-mp="3d82czukj07lk">
                {editableFields.map(field => {
              const Icon = fieldIcons[field.id];
              const active = selected.includes(field.id);
              return <li key={field.id} data-id="element-532" data-mp="39d94xu6uga4i">
                      <button type="button" onClick={() => setSelected(list => toggle(list, field.id))} aria-pressed={active} className={`relative flex h-full w-full flex-col items-start gap-2 rounded-2xl border p-4 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-533" data-mp="39c6bbei4ztkw">
                        <span className={`grid h-9 w-9 place-items-center rounded-xl ${active ? 'bg-ville-indigo text-white' : 'bg-slate-100 text-ville-navy'}`} aria-hidden="true" data-id="element-534" data-mp="2lnwy30yzoe5d">
                          <Icon className="h-4 w-4" data-id="element-535" data-mp="2zheh8knrjouv" />
                        </span>
                        <span className="text-[15px] font-bold text-ville-navy" data-id="element-536" data-mp="2una7xur0z4qm">{field.label}</span>
                        <span className="text-xs text-slate-500" data-id="element-537" data-mp="26x7jrpq8eixa">{current[field.id]}</span>
                        {active && <span className="absolute right-3 top-3 grid h-5 w-5 place-items-center rounded-full bg-ville-indigo text-white" aria-hidden="true" data-id="element-538" data-mp="21znulurzwlqk">
                            <CheckIcon className="h-3.5 w-3.5" data-id="element-539" data-mp="m8va2jl1wk6o" />
                          </span>}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {step === 'formulaire' && <div className="space-y-4" data-id="element-540" data-mp="dgteaylxfs2q">
              {chosen.filter(field => field.id !== 'adresse').map(field => <div key={field.id} data-id="element-541" data-mp="116dx9hi2ypc0">
                    <label htmlFor={`champ-${field.id}`} className="block text-sm font-bold text-ville-navy" data-id="element-542" data-mp="157hcc1sb0hc6">
                      {field.label}
                    </label>
                    <input id={`champ-${field.id}`} type="text" value={values[field.id] ?? ''} onChange={event => setValues({
              ...values,
              [field.id]: event.target.value
            })} placeholder={field.placeholder} className="mt-1.5 w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-543" data-mp="r9st0ydvxi98" />
                    <p className="mt-1 text-xs text-slate-500" data-id="element-544" data-mp="2sulr9gbr2twy">Valeur actuelle : {current[field.id]}</p>
                  </div>)}

              {addressSelected && <fieldset data-id="element-545" data-mp="2j3iq6eitmexo">
                  <legend className="text-sm font-bold text-ville-navy" data-id="element-546" data-mp="2j40snsw2tpe1">Adresse</legend>
                  <ul className="mt-2 space-y-2" data-id="element-547" data-mp="2e461do8e2xsg">
                    {verifiedAddresses.map(address => {
                const active = addressChoice === address.id;
                return <li key={address.id} data-id="element-548" data-mp="297o3977kdxp3">
                          <button type="button" onClick={() => setAddressChoice(address.id)} aria-pressed={active} className={`flex w-full items-start gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${active ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-slate-200 hover:border-ville-indigo'}`} data-id="element-549" data-mp="252y9pp0dov7c">
                            <MapPinIcon className={`mt-0.5 h-5 w-5 shrink-0 ${active ? 'text-ville-indigo' : 'text-slate-400'}`} aria-hidden="true" data-id="element-550" data-mp="5vzxpxwf99je" />
                            <span className="min-w-0" data-id="element-551" data-mp="fmm3l0ec6low">
                              <span className="block text-[15px] font-semibold text-ville-navy" data-id="element-552" data-mp="fn4cawi6qsdp">{address.label}</span>
                              <span className="block text-xs text-slate-500" data-id="element-553" data-mp="knrzf73amp2n">{address.note}</span>
                            </span>
                          </button>
                        </li>;
              })}

                    <li data-id="element-554" data-mp="3o48v16qbd3jr">
                      <button type="button" onClick={() => setAddressChoice('nouvelle')} aria-pressed={newAddressChosen} className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-3 text-left transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${newAddressChosen ? 'border-ville-indigo bg-ville-indigo-soft' : 'border-dashed border-slate-300 hover:border-ville-indigo'}`} data-id="element-555" data-mp="3t0zabruanv44">
                        <PlusIcon className={`h-5 w-5 shrink-0 ${newAddressChosen ? 'text-ville-indigo' : 'text-slate-400'}`} aria-hidden="true" data-id="element-556" data-mp="1yex44gvacb3x" />
                        <span className="text-[15px] font-semibold text-ville-navy" data-id="element-557" data-mp="283yhs0ecygeq">Ajouter une nouvelle adresse</span>
                      </button>
                    </li>
                  </ul>

                  {newAddressChosen && <div className="mt-3" data-id="element-558" data-mp="2h43m21o98yoo">
                      <label htmlFor="nouvelle-adresse" className="sr-only" data-id="element-559" data-mp="2h322t9x355vv">
                        Nouvelle adresse
                      </label>
                      <input id="nouvelle-adresse" type="text" value={newAddress} onChange={event => setNewAddress(event.target.value)} placeholder="Numéro, rue, municipalité" className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-[15px] text-ville-navy placeholder:text-slate-400 focus:border-ville-indigo focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-560" data-mp="2r3m2i84yi2bz" />
                      <p className="mt-1 text-xs text-slate-500" data-id="element-561" data-mp="33csbogyo1kf6">
                        Une nouvelle adresse doit être vérifiée par la Ville à l’étape suivante.
                      </p>
                    </div>}

                  <div className="mt-4 rounded-2xl bg-slate-50 px-5 py-4" data-id="element-562" data-mp="2tmh5sovf915n">
                    <p className="text-sm font-bold text-ville-navy" data-id="element-563" data-mp="2ymbt6ifzn8ly">Modifier aussi pour…</p>
                    <ul className="mt-2 space-y-1.5" data-id="element-564" data-mp="2kn2gvtuwkyn9">
                      {others.map(item => {
                  const active = alsoFor.includes(item.id);
                  return <li key={item.id} data-id="element-565" data-mp="2opqsg9b04jnx">
                            <button type="button" onClick={() => setAlsoFor(list => toggle(list, item.id))} aria-pressed={active} disabled={!item.canEdit} className="flex w-full items-center gap-3 rounded-xl px-2 py-1.5 text-left transition-colors duration-150 ease-ville hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-transparent" data-id="element-566" data-mp="2ap66535vo5po">
                              <span className={`grid h-5 w-5 shrink-0 place-items-center rounded border ${active ? 'border-ville-indigo bg-ville-indigo text-white' : item.canEdit ? 'border-slate-300 bg-white' : 'border-slate-200 bg-slate-100'}`} aria-hidden="true" data-id="element-567" data-mp="3f5uxjlb212xd">
                                {active && <CheckIcon className="h-3.5 w-3.5" data-id="element-568" data-mp="3b6vvq9kmdma5" />}
                              </span>
                              <img src={item.photo} alt="" className="h-7 w-7 rounded-full object-cover" data-id="element-569" data-mp="2r2bbjn63qnt" />
                              <span className="text-sm font-semibold text-ville-navy" data-id="element-570" data-mp="6qb9r1t5kcpp">{item.firstName}</span>
                              {!item.canEdit && <span className="ml-auto text-xs text-slate-500" data-id="element-571" data-mp="3ozbp0inpy5xv">Consentement requis</span>}
                            </button>
                          </li>;
                })}
                    </ul>
                  </div>
                </fieldset>}
            </div>}

          {step === 'preuves' && needProof.length === 0 && <div className="text-center" data-id="element-572" data-mp="2axdz4a2q0czn">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-573" data-mp="2awb7u11hk8j7">
                <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-574" data-mp="260njtoyqplfa" />
              </span>
              <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-575" data-mp="2jw83wnq7g9oa">
                Modifications enregistrées
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-576" data-mp="1r8viigvs67wk">
                Votre carte est à jour. Les changements sont visibles immédiatement.
              </p>
              {alsoFor.length > 0 && <p className="mt-2 text-sm text-slate-500" data-id="element-577" data-mp="20zq7ht0evllp">
                  Appliqué aussi à{' '}
                  {alsoFor.map(id => familyMembers.find(item => item.id === id)?.firstName).filter(Boolean).join(', ')}
                  .
                </p>}
            </div>}

          {step === 'preuves' && needProof.length > 0 && <div data-id="element-578" data-mp="13jja3j8a23vb">
              <p className="text-sm text-slate-600" data-id="element-579" data-mp="13hzuluug3wul">
                Ces renseignements doivent être validés par la Ville. Joignez un document pour chacun.
              </p>
              <ul className="mt-4 space-y-3" data-id="element-580" data-mp="38k3rzc1y66tu">
                {needProof.map(field => {
              const done = proofs.includes(field.id);
              return <li key={field.id} className="rounded-2xl border border-slate-200 px-5 py-4" data-id="element-581" data-mp="3ib86ybrmhroo">
                      <p className="text-[15px] font-bold text-ville-navy" data-id="element-582" data-mp="2kng16tei1i5h">{field.label}</p>
                      <p className="mt-0.5 text-sm text-slate-500" data-id="element-583" data-mp="2pmj52aiqtibv">{field.proofLabel}</p>
                      <button type="button" onClick={() => setProofs(list => toggle(list, field.id))} className={`mt-3 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-bold transition-colors duration-150 ease-ville focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo ${done ? 'bg-ville-indigo-soft text-ville-indigo' : 'border border-slate-200 text-ville-navy hover:border-ville-indigo hover:text-ville-indigo'}`} data-id="element-584" data-mp="2podrjt9nxii2">
                        {done ? <CheckIcon className="h-4 w-4" data-id="element-585" data-mp="1ydr3hcia5ve1" /> : <UploadIcon className="h-4 w-4" data-id="element-586" data-mp="1yh58ksibwip6" />}
                        {done ? 'Document joint' : 'Joindre un document'}
                      </button>
                    </li>;
            })}
              </ul>
            </div>}

          {step === 'confirmation' && <div className="text-center" data-id="element-587" data-mp="1kmumyk1mp4nc">
              <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-588" data-mp="1klt9k8x5462g">
                <ShieldCheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-589" data-mp="1aoye5yw5ylse" />
              </span>
              <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-590" data-mp="16nctvshav586">
                {needProof.length > 0 ? 'Demande transmise' : 'Modifications enregistrées'}
              </h3>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-591" data-mp="312fgnieys97e">
                {needProof.length > 0 ? 'La Ville validera les documents fournis. Vous serez avisé dès que la carte sera mise à jour.' : 'Votre carte est à jour. Les changements sont visibles immédiatement.'}
              </p>
              {alsoFor.length > 0 && <p className="mt-2 text-sm text-slate-500" data-id="element-592" data-mp="1xpovzw75l8zy">
                  Appliqué aussi à{' '}
                  {alsoFor.map(id => familyMembers.find(item => item.id === id)?.firstName).filter(Boolean).join(', ')}
                  .
                </p>}
            </div>}
        </motion.div>
      </AnimatePresence>

      <div className="mt-7 flex items-center justify-between gap-4" data-id="element-593" data-mp="1a02wf7mvcrrf">
        {step === 'confirmation' || step === 'preuves' && needProof.length === 0 ? <button type="button" onClick={onClose} className="ml-auto rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-594" data-mp="110za7usenw8p">
            Terminer
          </button> : <>
            <button type="button" onClick={back} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-595" data-mp="w0uva975b9kl">
              {step === 'champs' ? 'Annuler' : 'Retour'}
            </button>
            <button type="button" onClick={next} disabled={step === 'champs' && selected.length === 0} className="rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:hover:translate-y-0" data-id="element-596" data-mp="mbxp95t6sqki">
              {step === 'formulaire' && needProof.length === 0 ? 'Enregistrer' : step === 'preuves' ? 'Transmettre' : 'Continuer'}

            </button>
          </>}
      </div>
    </Shell>;
}
interface ConsentDialogProps {
  member: FamilyMember | null;
  onClose: () => void;
}
export function ConsentDialog({
  member,
  onClose
}: ConsentDialogProps) {
  const [sent, setSent] = useState(false);
  useEffect(() => {
    if (member) setSent(false);
  }, [member]);
  if (!member) return null;
  return <Shell title={`Modifier la carte de ${member.firstName}`} onClose={onClose} data-id="element-597" data-mp="1r58y3xum44st">
      {sent ? <div className="text-center" data-id="element-598" data-mp="1vvp97baynec0">
          <span className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-ville-indigo-soft text-ville-indigo" data-id="element-599" data-mp="1vwqxihgzlxbo">
            <CheckIcon className="h-7 w-7" aria-hidden="true" data-id="element-600" data-mp="1zwrf2azduomy" />
          </span>
          <h3 className="mt-4 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-601" data-mp="24vc9tuvv8g40">Demande envoyée</h3>
          <p className="mt-2 text-[15px] leading-relaxed text-slate-600" data-id="element-602" data-mp="29wrx7tlimypk">
            {member.firstName} recevra une notification et pourra vous autoriser à modifier sa carte.
          </p>
        </div> : <div className="flex items-start gap-4" data-id="element-603" data-mp="fir0be9j0ls4">
          <img src={member.photo} alt="" className="h-14 w-14 shrink-0 rounded-full object-cover" data-id="element-604" data-mp="kgitscptkfyf" />
          <p className="text-[15px] leading-relaxed text-slate-600" data-id="element-605" data-mp="2xqsbxhpggsxe">
            Vous n’avez pas le consentement de {member.firstName} pour modifier sa carte.
          </p>
        </div>}

      <div className="mt-7 flex items-center justify-between gap-4" data-id="element-606" data-mp="2sy8yckqrpxsl">
        <button type="button" onClick={onClose} className="text-sm font-semibold text-slate-500 transition-colors duration-150 ease-ville hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-607" data-mp="2oz03fcjzrtvg">
          {sent ? 'Fermer' : 'Annuler'}
        </button>
        {!sent && <button type="button" onClick={() => setSent(true)} className="inline-flex items-center gap-2 rounded-full bg-ville-navy px-6 py-3 text-sm font-bold text-white transition-transform duration-150 ease-ville hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo focus-visible:ring-offset-2" data-id="element-608" data-mp="3coknjyrvsx6b">
            <SendIcon className="h-4 w-4" aria-hidden="true" data-id="element-609" data-mp="1raq1syo4b84v" />
            Demander son consentement
          </button>}
      </div>
    </Shell>;
}
function Shell({
  title,
  onClose,
  children
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return <motion.div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease
  }} data-id="element-610" data-mp="4lfnrq19qkw">
      <button type="button" aria-label="Fermer" onClick={onClose} className="absolute inset-0 bg-ville-navy/40" data-id="element-611" data-mp="dycu9eimg0nh" />
      <motion.div role="dialog" aria-modal="true" aria-label={title} initial={{
      y: 20,
      opacity: 0,
      scale: 0.98
    }} animate={{
      y: 0,
      opacity: 1,
      scale: 1
    }} exit={{
      y: 12,
      opacity: 0
    }} transition={{
      duration: 0.25,
      ease
    }} className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-t-3xl bg-white p-7 shadow-2xl sm:rounded-3xl" data-id="element-612" data-mp="ixfmfydi6ce1">
        <button type="button" onClick={onClose} aria-label="Fermer" className="absolute right-4 top-4 grid h-10 w-10 place-items-center rounded-full text-slate-500 transition-colors duration-150 ease-ville hover:bg-slate-100 hover:text-ville-navy focus:outline-none focus-visible:ring-2 focus-visible:ring-ville-indigo" data-id="element-613" data-mp="11ofhnhblcifr">
          <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-614" data-mp="2w32q7vllm06e" />
        </button>
        <h2 className="pr-10 text-xl font-extrabold tracking-tight text-ville-navy" data-id="element-615" data-mp="2r5ub63vqyc9d">{title}</h2>
        <div className="mt-5" data-id="element-616" data-mp="23g9mr5gtqmsp">{children}</div>
      </motion.div>
    </motion.div>;
}